import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Flex, Text } from "@chakra-ui/react";

// Carousel "Tin tức nội bộ" bám theo InfinityNewsSlider của app:
// card active căn giữa (scale 1), card hai bên thu nhỏ & ló ra, cuộn lặp vô hạn.
const COPIES = 9;
const MID = Math.floor(COPIES / 2);
const WIDTH_RATIO = 0.58; // bề rộng card = 58% khung nhìn
const GAP = 1;
const HEIGHT = 150;
const SIDE_SCALE = 0.86;
const SIDE_SCALE_Y = 0.82;

export default function NewsSlider({ items = [] }) {
  const ref = useRef(null);
  const resettingRef = useRef(false);
  const rafRef = useRef(0);
  const [containerW, setContainerW] = useState(0);
  const [scrollX, setScrollX] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setContainerW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = items.length;
  const cardW = containerW ? containerW * WIDTH_RATIO : 0;
  const snap = cardW + GAP;
  const sidePad = containerW ? (containerW - cardW) / 2 : 0;
  const initialIndex = n ? MID * n : 0;

  const data = useMemo(() => {
    if (!n) return [];
    const big = [];
    for (let c = 0; c < COPIES; c += 1) {
      items.forEach((x, i) => big.push({ ...x, _k: `${x.id}-${c}-${i}` }));
    }
    return big;
  }, [items, n]);

  // Đưa về giữa dải copy khi đã biết kích thước (và khi resize).
  useEffect(() => {
    const el = ref.current;
    if (!el || !snap) return;
    el.scrollLeft = initialIndex * snap;
    setScrollX(el.scrollLeft);
  }, [snap, initialIndex]);

  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setScrollX(el.scrollLeft));

    if (resettingRef.current || !n || !snap) return;
    const idx = Math.round(el.scrollLeft / snap);
    const minIdx = (MID - 2) * n;
    const maxIdx = (MID + 2) * n;
    if (idx < minIdx || idx > maxIdx) {
      const mod = ((idx % n) + n) % n;
      const target = (initialIndex + mod) * snap;
      resettingRef.current = true;
      el.scrollLeft = target;
      setScrollX(target);
      requestAnimationFrame(() => {
        resettingRef.current = false;
      });
    }
  }, [n, snap, initialIndex]);

  if (!n) return null;

  return (
    <Box
      ref={ref}
      mt="12px"
      mx="-20px"
      overflowX="auto"
      onScroll={onScroll}
      css={{
        scrollSnapType: "x mandatory",
        WebkitOverflowScrolling: "touch",
        "::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
      }}
    >
      <Flex px={`${sidePad}px`} pt="12px" pb="16px" w="max-content">
        {data.map((item, index) => {
          const dist = snap ? Math.abs(index * snap - scrollX) / snap : 0;
          const t = Math.max(0, 1 - Math.min(dist, 1)); // 1 ở giữa, 0 ở cạnh
          const sx = SIDE_SCALE + (1 - SIDE_SCALE) * t;
          const sy = SIDE_SCALE_Y + (1 - SIDE_SCALE_Y) * t;
          return (
            <Flex
              key={item._k}
              flex="0 0 auto"
              w={`${cardW}px`}
              mr={`${GAP}px`}
              h={`${HEIGHT}px`}
              align="center"
              justify="center"
              css={{ scrollSnapAlign: "center" }}
            >
              <Box
                as="button"
                type="button"
                onClick={item.onPress}
                draggable={false}
                position="relative"
                w="100%"
                h={`${HEIGHT}px`}
                borderRadius="16px"
                overflow="hidden"
                border="1px solid rgba(8,20,45,0.06)"
                boxShadow="0 3px 8px rgba(8,20,45,0.12)"
                backgroundImage={`url(${item.image})`}
                backgroundSize="cover"
                backgroundPosition="center"
                transform={`scale(${sx}, ${sy})`}
                transition="transform 0.05s linear"
              >
                <Flex position="absolute" inset="0" align="center" justify="center">
                  <Text
                    color="white"
                    fontSize="20px"
                    fontWeight={800}
                    textShadow="0 2px 6px rgba(0,0,0,0.45)"
                  >
                    {item.title}
                  </Text>
                </Flex>
              </Box>
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
}
